import React, { useEffect, useState } from "react";
import { Sparkles, Flame } from "lucide-react";

/**
 * Compact live countdown used inside urgency banners.
 * Renders J · H · M · S with tight spacing so it fits alongside body text.
 */
const CompactCountdown = ({ target, accent = "gold" }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor((diff / 3_600_000) % 24);
  const m = Math.floor((diff / 60_000) % 60);
  const s = Math.floor((diff / 1000) % 60);
  const pad = (n) => String(n).padStart(2, "0");
  const accentClass = accent === "red" ? "text-red-400" : "text-[color:var(--gold)]";
  const units = [
    { l: "J", v: pad(d) },
    { l: "H", v: pad(h) },
    { l: "M", v: pad(m) },
    { l: "S", v: pad(s) },
  ];
  return (
    <div data-testid="compact-countdown" className="flex items-center gap-2 md:gap-3 shrink-0">
      {units.map((u, i) => (
        <React.Fragment key={u.l}>
          <div className="flex flex-col items-center min-w-[38px] md:min-w-[44px]">
            <span className={`font-serif text-xl md:text-2xl tabular-nums leading-none ${accentClass}`}>{u.v}</span>
            <span className="label-caps text-white/40 mt-1 text-[9px] tracking-[0.2em]">{u.l}</span>
          </div>
          {i < units.length - 1 && <span className="font-serif text-lg text-white/20 leading-none">:</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Urgency banner shown on product pages when the product carries a
 * `limited_edition_until` ISO timestamp in the future. Displays a compact
 * live countdown + luxury copy encouraging fast checkout.
 */
const LimitedEditionBanner = ({ untilIso, stock }) => {
  const target = untilIso ? new Date(untilIso).getTime() : 0;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [target]);

  if (!target || target <= now) return null;

  const remainingHours = Math.floor((target - now) / 3_600_000);
  const isCritical = remainingHours < 24;
  const accent = isCritical ? "red" : "gold";

  return (
    <div
      data-testid="limited-edition-banner"
      className={`relative overflow-hidden border ${
        isCritical ? "border-red-500/30 bg-red-500/[0.03]" : "border-[color:var(--gold)]/25 bg-[color:var(--gold)]/[0.03]"
      } mb-8`}
    >
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: isCritical
            ? "repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(239,68,68,0.4) 12px, rgba(239,68,68,0.4) 13px)"
            : "repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(212,175,55,0.35) 12px, rgba(212,175,55,0.35) 13px)",
        }}
      />
      <div className="relative px-5 py-5 md:px-6 md:py-6">
        <div className="flex items-center gap-3 md:gap-4 mb-4">
          <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            isCritical ? "bg-red-500/10 text-red-400" : "bg-[color:var(--gold)]/10 text-[color:var(--gold)]"
          }`}>
            {isCritical ? <Flame size={18} strokeWidth={1.5} /> : <Sparkles size={18} strokeWidth={1.5} />}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`label-caps text-[10px] tracking-[0.3em] mb-1 ${
              isCritical ? "text-red-400" : "text-[color:var(--gold)]"
            }`}>
              Édition limitée · {isCritical ? "Dernières heures" : "Fin d'exclusivité"}
            </p>
            <p className="font-serif text-white text-base md:text-lg leading-tight">
              {isCritical ? "Il ne reste que quelques instants." : "Une pièce éphémère — bientôt retirée de l'écrin."}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <CompactCountdown target={target} accent={accent} />
          {typeof stock === "number" && stock > 0 && stock <= 10 && (
            <p className="text-xs text-white/50">
              Il reste seulement <span className={isCritical ? "text-red-400" : "text-[color:var(--gold)]"}>{stock} flacon{stock > 1 ? "s" : ""}</span> disponibles.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LimitedEditionBanner;
